/* ==========================================================================
   Corium 21 · Canvas · Shopify Forms teaser flagger
   (form 1129560 today; launch form 1207174 after the swap)
   File: assets/forms-teaser-flag.js, loaded from layout/theme.liquid
   just before </body>:
     <script src="{{ 'forms-teaser-flag.js' | asset_url }}" defer></script>
   Why JS is needed: Forms uses ONE fixed element (#app-embed, inside the open
   shadow root of <shopify-forms-embed>) for both the modal and the teaser, and
   nothing outside the shadow root changes between the two states. CSS on the
   host alone can't tell them apart, so any move/scale/hide/restyle rule would
   also hit the modal. This script READS the shadow DOM and sets attributes on
   the host:
     data-c21-teaser       present while the small teaser (not the modal) shows
                           (both forms; base.css styles the teaser from it)
     data-c21-teaser-hide  1129560 only (bottom-left teaser). Space-separated:
                           "buy"   the main product's variant picker or buy
                                   buttons sit under the teaser (<=749px only)
                           "cards" a "You may also like" product card sits
                                   under the teaser (all widths)
                           "text"  any visible text line inside <main> (product
                                   title, description, "How to use" and other
                                   accordion rows, headings) sits under the
                                   teaser (all widths). The teaser is fixed
                                   bottom-left and phone content is full width,
                                   so no fixed spot clears it; it steps aside
                                   instead. Fade rule injected below.
                           "footer" a footer link/field sits under the
                                   teaser (all widths)
                           "end"   the footer's last row (bottom bar) is in
                                   view: gone at the page bottom, back on
                                   scroll-up (all widths). Same test as
                                   assets/c21-chat-dock.js "end"; its fade
                                   rule is injected below (END_STYLE_ID).
   It writes ONE thing into the shadow root: a small <style>, using stable
   attribute selectors (#app-embed / data-testid / role / aria-label), not
   hashed classes, that
     - lets taps through the teaser's transparent wrapper (Forms makes the
       whole 210x52 row tappable, 16px wider than the pill on each side);
       only the visible pill keeps pointer events
     - gives the teaser's close mark (12x15px) an invisible 44x47px hit area
     - 1129560 modal only: moves the 28px close mark into the card corner
       (8px in, was 30px) so the heading clears it, and gives it a 44x44 hit area.
   It never clicks, closes, or changes Forms' own elements or text.
   Desktop wheel pass-through (>=990px, popup modal open only): Canvas scrolls
   div.page-wrapper there (base.css sets html/body overflow:hidden), and the
   Forms host sits outside it, so a wheel over the open popup's overlay scrolled
   nothing. A passive wheel listener on the host forwards the delta to
   .page-wrapper, the same as the document scroll below 990px. It skips a popup
   card that scrolls internally and never calls preventDefault.
   ========================================================================== */
(() => {
  const FORMS = [
    { id: '1129560', hide: true },  // live form, bottom-left teaser
    { id: '1207174', hide: true },  // launch form, bottom-left teaser since the 10-25 Forms fix: same hide rules as 1129560
  ];
  const MOBILE = window.matchMedia('(max-width: 749px)'); // same breakpoint as the Inbox block
  const DESKTOP = window.matchMedia('(min-width: 990px)'); // base.css: .page-wrapper is the scroller from here up
  const GAP_PX = 8; // keep this much clear space around the teaser
  const CLOSE_STYLE_ID = 'c21-teaser-close-hit';
  const CLOSE_CSS =
    '#app-embed > div:has(> [data-testid="form-teaser"]){pointer-events:none}' +
    '[data-testid="form-teaser"]{pointer-events:auto}' +
    '[data-testid="form-teaser"] > [role="button"][aria-label="Close modal"]{position:relative}' +
    '[data-testid="form-teaser"] > [role="button"][aria-label="Close modal"]::after{' +
    'content:"";position:absolute;inset:-16px -24px -16px -8px}' +
    // 1129560 MODAL close (not the teaser's), all widths: move the 28px mark into the card corner
    // (8px from the top and right edges instead of 30px) so the one-line, centered heading clears it,
    // and give it an invisible 44x44 hit area. The mark itself keeps its size and look.
    ':host(#app-embed-container-1129560) #app-embed div:not([data-testid="form-teaser"]):has(> [role="button"][aria-label="Close modal"]){top:8px;right:8px}' +
    ':host(#app-embed-container-1129560) #app-embed div:not([data-testid="form-teaser"]) > [role="button"][aria-label="Close modal"]{position:relative}' +
    ':host(#app-embed-container-1129560) #app-embed div:not([data-testid="form-teaser"]) > [role="button"][aria-label="Close modal"]::after{' +
    'content:"";position:absolute;inset:-8px}' +
    // 1207174 MODAL close: same move and hit area as 1129560's above
    ':host(#app-embed-container-1207174) #app-embed div:not([data-testid="form-teaser"]):has(> [role="button"][aria-label="Close modal"]){top:8px;right:8px}' +
    ':host(#app-embed-container-1207174) #app-embed div:not([data-testid="form-teaser"]) > [role="button"][aria-label="Close modal"]{position:relative}' +
    ':host(#app-embed-container-1207174) #app-embed div:not([data-testid="form-teaser"]) > [role="button"][aria-label="Close modal"]::after{' +
    'content:"";position:absolute;inset:-8px}';
  // "end" and "text" fade: same opacity/visibility/pointer-events/transition as base.css's
  // cards/footer rule for 1129560, put in the document head like c21-chat-dock.js does.
  const END_STYLE_ID = 'c21-teaser-end-styles';
  const END_CSS =
    '#app-embed-container-1129560[data-c21-teaser][data-c21-teaser-hide~="end"],' +
    '#app-embed-container-1129560[data-c21-teaser][data-c21-teaser-hide~="text"],' +
    '#app-embed-container-1207174[data-c21-teaser][data-c21-teaser-hide~="end"],' +
    '#app-embed-container-1207174[data-c21-teaser][data-c21-teaser-hide~="text"]{' +
    'opacity:0;visibility:hidden;pointer-events:none;' +
    'transition:opacity 0.2s ease,visibility 0s linear 0.2s}' +
    // 1207174 copies of base.css's 1129560-only teaser rules (base.css itself is unchanged):
    // fade transition, "buy" (<=749px, any value), "cards"/"footer" fades, and the
    // sticky add-to-cart lift (<=899px) that keeps the teaser above the bar.
    '#app-embed-container-1207174[data-c21-teaser]{transition:opacity 0.2s ease}' +
    '@media screen and (max-width:749px){#app-embed-container-1207174[data-c21-teaser][data-c21-teaser-hide]{' +
    'opacity:0;visibility:hidden;pointer-events:none;' +
    'transition:opacity 0.2s ease,visibility 0s linear 0.2s}}' +
    '#app-embed-container-1207174[data-c21-teaser][data-c21-teaser-hide~="cards"],' +
    '#app-embed-container-1207174[data-c21-teaser][data-c21-teaser-hide~="footer"]{' +
    'opacity:0;visibility:hidden;pointer-events:none;' +
    'transition:opacity 0.2s ease,visibility 0s linear 0.2s}' +
    '@media screen and (max-width:899px){body[data-sticky-atc-visible] #app-embed-container-1207174[data-c21-teaser]{' +
    'position:fixed;left:0;right:0;bottom:0;height:0;z-index:2147483647;' +
    'transform:translateY(calc(-1 * var(--sticky-atc-clearance, 75px)))}}';
  const hosts = new Map(); // host element -> config

  const teaserOf = (root) => {
    const el = root.querySelector('#app-embed');
    if (!el) return null;
    const t = root.querySelector('[data-testid="form-teaser"]');
    if (/Teaser/.test(el.className)) return t || el; // e.g. _appEmbedRootTeaser_xxxx
    const r = el.getBoundingClientRect(); // fallback if Forms renames classes
    return r.height > 0 && r.height < innerHeight * 0.4 && r.width < innerWidth * 0.8 ? (t || el) : null;
  };

  const ensureCloseStyle = (root) => {
    if (root.getElementById(CLOSE_STYLE_ID)) return;
    const s = document.createElement('style');
    s.id = CLOSE_STYLE_ID;
    s.textContent = CLOSE_CSS;
    root.appendChild(s);
  };

  const overlaps = (a, b) =>
    a.left - GAP_PX < b.right && a.right + GAP_PX > b.left &&
    a.top - GAP_PX < b.bottom && a.bottom + GAP_PX > b.top;

  const covered = (tr, selector) => {
    for (const e of document.querySelectorAll(selector)) {
      const r = e.getBoundingClientRect();
      if (r.width && overlaps(tr, r)) return true;
    }
    return false;
  };

  /** True while any visible text line inside <main> is within GAP_PX of the teaser box. */
  const textUnder = (tr) => {
    const main = document.querySelector('main');
    if (!main) return false;
    const w = document.createTreeWalker(main, NodeFilter.SHOW_TEXT);
    const rg = document.createRange();
    let n;
    while ((n = w.nextNode())) {
      if (!n.nodeValue.trim()) continue;
      const el = n.parentElement;
      if (el.checkVisibility && !el.checkVisibility({ opacityProperty: true, visibilityProperty: true })) continue;
      rg.selectNodeContents(n);
      for (const r of rg.getClientRects()) if (r.width > 1 && r.height > 1 && overlaps(tr, r)) return true;
    }
    return false;
  };

  /** True while the footer's last row (its bottom bar) is in the viewport (as c21-chat-dock.js). */
  const atEnd = () => {
    const sections = document.querySelectorAll('.shopify-section-group-footer-group');
    const last = sections[sections.length - 1];
    if (!last) return false;
    const r = last.getBoundingClientRect();
    return r.height > 0 && r.top < window.innerHeight && r.bottom > 0;
  };

  const applyHost = (h, cfg) => {
    const root = h.shadowRoot;
    if (!root) return;
    ensureCloseStyle(root);
    const t = teaserOf(root);
    h.toggleAttribute('data-c21-teaser', !!t);
    let hide = '';
    if (t && cfg.hide) {
      // the teaser keeps its box while hidden (visibility), so this stays stable
      const tr = t.getBoundingClientRect();
      const why = [];
      if (MOBILE.matches && covered(tr, 'main variant-picker, main .buy-buttons-block')) why.push('buy');
      if (covered(tr, 'main product-recommendations product-card')) why.push('cards');
      if (textUnder(tr)) why.push('text');
      if (covered(tr, '.shopify-section-group-footer-group :is(a, button, input)')) why.push('footer');
      if (atEnd()) why.push('end');
      hide = why.join(' ');
    }
    if (hide) h.setAttribute('data-c21-teaser-hide', hide);
    else h.removeAttribute('data-c21-teaser-hide');
  };

  /** Wheel over the open popup (>=990px): scroll .page-wrapper, as the document scrolls below 990px. */
  const passWheel = (e) => {
    if (!DESKTOP.matches || e.ctrlKey || !e.deltaY) return;
    const pw = document.querySelector('.page-wrapper');
    if (!pw) return;
    let overlay = false;
    for (const n of e.composedPath()) {
      if (n === e.currentTarget) break;
      if (n.nodeType !== 1) continue;
      const cs = getComputedStyle(n);
      if (/(auto|scroll)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return; // card scrolls itself
      if (cs.position === 'fixed') {
        const r = n.getBoundingClientRect();
        if (r.width >= innerWidth * 0.9 && r.height >= innerHeight * 0.9) overlay = true; // modal overlay, not the teaser
      }
    }
    if (!overlay) return;
    const k = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? innerHeight : 1;
    pw.scrollBy({ top: e.deltaY * k, left: 0, behavior: 'instant' });
  };

  let queued = false;
  const apply = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      hosts.forEach((cfg, h) => applyHost(h, cfg));
    });
  };

  const attach = (h, cfg) => {
    if (hosts.has(h)) return;
    const ready = () => {
      if (!h.shadowRoot) return false;
      hosts.set(h, cfg);
      h.addEventListener('wheel', passWheel, { passive: true });
      new MutationObserver(apply).observe(h.shadowRoot, {
        subtree: true, childList: true, attributes: true, attributeFilter: ['class'],
      });
      apply();
      return true;
    };
    if (!ready()) {
      let tries = 0;
      const t = setInterval(() => { if (ready() || ++tries > 80) clearInterval(t); }, 250);
    }
  };

  const scan = () => {
    for (const cfg of FORMS) {
      const h = document.querySelector(`shopify-forms-embed#app-embed-container-${cfg.id}`);
      if (h) attach(h, cfg);
    }
    return hosts.size === FORMS.length;
  };

  const init = () => {
    if (!document.getElementById(END_STYLE_ID)) {
      const s = document.createElement('style');
      s.id = END_STYLE_ID;
      s.textContent = END_CSS;
      document.head.append(s);
    }
    // capture: at desktop widths Canvas scrolls div.page-wrapper, not the window,
    // and element scroll events don't bubble
    document.addEventListener('scroll', apply, { passive: true, capture: true });
    addEventListener('resize', apply);
    MOBILE.addEventListener('change', apply);
    // sticky add-to-cart bar moves the teaser; lazy recommendations add cards
    new MutationObserver(apply).observe(document.body, { attributes: true, attributeFilter: ['data-sticky-atc-visible'] });
    const recs = document.querySelector('main product-recommendations');
    if (recs) new MutationObserver(apply).observe(recs, { childList: true, subtree: true });
    // page height changes (lazy sections, images) can bring the footer's last row in or out of view
    const ro = new ResizeObserver(apply);
    ro.observe(document.documentElement);
    ro.observe(document.body);
    const main = document.querySelector('main');
    if (main) ro.observe(main);
    if (scan()) return;
    const mo = new MutationObserver(() => { if (scan()) mo.disconnect(); });
    mo.observe(document.body, { childList: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
